'use client'

import Link from 'next/link'
import type { User } from '@supabase/supabase-js'
import { Tables } from '@/utils/schema'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  PencilEdit01Icon,
  Logout01Icon,
  UserIcon,
  WorkIcon as WorkStrokeIcon,
  DashboardSpeed01Icon as DashboardSpeed01StrokeIcon,
} from '@hugeicons-pro/core-stroke-rounded'
import { createClient } from '@/utils/supabase/client'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTheme } from 'next-themes'
import { HugeiconsIcon } from '@hugeicons/react'
import { Sun03Icon, Moon02Icon, ComputerIcon } from '@hugeicons-pro/core-solid-rounded'

function ThemeButton({ icon, label, value }: { icon: any; label: string; value: string }) {
  const { theme, setTheme } = useTheme()
  const isActive = theme === value
  return (
    <button
      onClick={() => setTheme(value)}
      className={cn(
        'flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors',
        isActive
          ? 'bg-atrium-content text-atrium-black dark:bg-atrium-inner'
          : 'text-atrium-black/50 hover:text-atrium-black hover:bg-atrium-content/50'
      )}
    >
      <HugeiconsIcon icon={icon} className="size-3.5" />
      {label}
    </button>
  )
}

export type SidebarRailMode = 'expanded' | 'collapsed' | 'overlay'

export type AppSidebarUserMenuProps = {
  expanded: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  user: User
  publicProfile: Tables<'public_profiles'> | null
  avatarUrl: string | null
  /** True while the public profile request is in flight. */
  profileLoading: boolean
  setAccountOpen: (open: boolean) => void
  /**
   * When true, render the Atrium-staff-only Work + Admin links in the menu.
   * Computed by the parent sidebar from the user's email domain. Gated by
   * `!!user?.email?.endsWith('@atrium.run')`. Moved out of the sidebar nav
   * (2026-05-22) so the customer-facing nav stays clean for staff users
   * who are logged into a customer-owned workspace.
   */
  showAdminNav?: boolean
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left'
}

export function AppSidebarUserMenu(props: AppSidebarUserMenuProps) {
  const {
    expanded,
    open,
    onOpenChange,
    user,
    publicProfile,
    avatarUrl,
    profileLoading,
    setAccountOpen,
    showAdminNav = false,
    tooltipSide = 'right',
  } = props

  const compact = !expanded
  const supabase = createClient()

  const loading = profileLoading
  const name = !profileLoading ? publicProfile?.display_name?.trim() ?? null : null
  const triggerAria = name ? `Account: ${name}` : 'Account'

  const initial = name?.[0]?.toUpperCase() ?? ''

  const handleEditProfile = () => {
    onOpenChange(false)
    setAccountOpen(true)
  }

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut()
      onOpenChange(false)
      window.location.href = '/'
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const triggerButton = (
    <button
      type="button"
      aria-label={triggerAria}
      aria-haspopup="dialog"
      aria-expanded={open}
      className="flex size-8 shrink-0 cursor-inherit items-center justify-center rounded-full hover:ring-2 hover:ring-atrium-border transition-all"
    >
      {loading ? (
        <Skeleton className="size-8 rounded-full bg-atrium-border/60" />
      ) : (
        <Avatar className="size-8 rounded-full">
          <AvatarImage src={avatarUrl || undefined} className="object-cover" />
          <AvatarFallback className="flex size-full items-center justify-center rounded-full bg-atrium-content text-xs font-medium text-atrium-muted">
            {initial || <HugeiconsIcon icon={UserIcon} className="size-4 opacity-60" />}
          </AvatarFallback>
        </Avatar>
      )}
    </button>
  )

  return (
    <div
      className={cn(
        'relative z-10 flex min-w-0 flex-col',
        expanded ? 'w-auto items-start translate-x-[4px]' : 'w-auto items-center translate-x-[2px]'
      )}
    >
      <Popover open={open} onOpenChange={onOpenChange}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side={tooltipSide}>
            {name ? (
              <span className="max-w-[220px] font-normal">{name}</span>
            ) : (
              <span className="font-normal text-[#939294]">Account</span>
            )}
          </TooltipContent>
        </Tooltip>
        <PopoverContent
          className="z-[110] w-72 rounded-xl border border-atrium-border bg-popover p-0 text-popover-foreground shadow-lg"
          align="start"
          side="top"
          sideOffset={8}
        >
          {/* Theme options */}
          <div className="flex items-center gap-1 border-b border-atrium-border px-2 py-2">
            <ThemeButton icon={Sun03Icon} label="Light" value="light" />
            <ThemeButton icon={Moon02Icon} label="Dark" value="dark" />
            <ThemeButton icon={ComputerIcon} label="System" value="system" />
          </div>
          <div className="flex flex-col gap-0.5 p-1.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 w-full justify-start gap-2 px-2 text-sm font-normal text-atrium-black hover:bg-atrium-content dark:hover:bg-atrium-inner"
              onClick={handleEditProfile}
            >
              <HugeiconsIcon icon={PencilEdit01Icon} className="size-3.5 shrink-0" />
              Edit profile
            </Button>

            {showAdminNav ? (
              <>
                <div className="my-1 h-px bg-atrium-border" />
                <div className="px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-atrium-black/40">
                  Atrium
                </div>
                <Button
                  asChild
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-9 w-full justify-start gap-2 px-2 text-sm font-normal text-atrium-black hover:bg-atrium-content dark:hover:bg-atrium-inner"
                >
                  <Link href="/work" onClick={() => onOpenChange(false)}>
                    <HugeiconsIcon icon={WorkStrokeIcon} className="size-3.5 shrink-0" />
                    Work
                  </Link>
                </Button>
                <Button
                  asChild
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-9 w-full justify-start gap-2 px-2 text-sm font-normal text-atrium-black hover:bg-atrium-content dark:hover:bg-atrium-inner"
                >
                  <Link href="/admin" onClick={() => onOpenChange(false)}>
                    <HugeiconsIcon icon={DashboardSpeed01StrokeIcon} className="size-3.5 shrink-0" />
                    Admin
                  </Link>
                </Button>
                <div className="my-1 h-px bg-atrium-border" />
              </>
            ) : null}

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 w-full justify-start gap-2 px-2 text-sm font-normal text-atrium-black hover:bg-atrium-content dark:hover:bg-atrium-inner"
              onClick={handleSignOut}
            >
              <HugeiconsIcon icon={Logout01Icon} className="size-3.5 shrink-0" />
              Log out
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
